import { useState, useEffect } from 'react';
import AuthGuard from '@/components/feature/AuthGuard';
import AppLayout from '@/components/feature/AppLayout';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import { supabase } from '@/lib/supabase';
import i18n from '@/i18n';
import { invalidateProfileCache } from '@/services/profile';
import { alotMethods, tzetMethods, type AlotMethod, type TzetMethod } from '@/lib/hebcal';
import tzlookup from 'tz-lookup';
import SubscriptionCard from './components/SubscriptionCard';

const languages = [
  { value: 'en', label: 'English', flag: '🇺🇸', dir: 'ltr' },
  { value: 'he', label: 'עברית', flag: '🇮🇱', dir: 'rtl' },
  { value: 'yi', label: 'ייִדיש', flag: '🗣️', dir: 'rtl' },
];

const timezones = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Jerusalem',
  'Asia/Tokyo',
  'Australia/Sydney',
];

function SettingsContent() {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const [profile, setProfile] = useState({
    fullName: user?.user_metadata?.full_name || '',
    email: user?.email || '',
    phone: '',
  });

  const [language, setLanguage] = useState('en');
  const [timezone, setTimezone] = useState('America/New_York');
  const [shabbatMode, setShabbatMode] = useState<'skip' | 'post_shabbat_report'>('skip');
  const [holidayMode, setHolidayMode] = useState<'skip' | 'post_holiday_report'>('skip');
  const [alotMethod, setAlotMethod] = useState<AlotMethod>('72min');
  const [tzetMethod, setTzetMethod] = useState<TzetMethod>('72min');
  const [location, setLocation] = useState({
    latitude: '40.7128',
    longitude: '-74.0060',
    elevation: '10',
    city: 'New York',
    country: 'United States',
    zip: '10001',
  });
  const [zipStatus, setZipStatus] = useState<'idle' | 'loading' | 'error'>('idle');

  // Call schedule
  interface CallSlot {
    id: string;
    dayOfWeek: number;
    time: string;
    enabled: boolean;
  }
  const [callSlots, setCallSlots] = useState<CallSlot[]>([]);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');

  const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // Load user data + call schedule from Supabase on mount
  useEffect(() => {
    async function loadAllData() {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) return;
        const userId = session.user.id;

        // Load profile
        const { data: dbUser } = await supabase
          .from('users')
          .select('phone_number, language, timezone, shabbat_mode, holiday_mode, latitude, longitude, elevation, alot_method, tzet_method')
          .eq('id', userId)
          .maybeSingle();

        if (dbUser) {
          if (dbUser.phone_number) setProfile((p) => ({ ...p, phone: dbUser.phone_number }));
          if (dbUser.language) {
            setLanguage(dbUser.language);
            if (i18n.language !== dbUser.language) i18n.changeLanguage(dbUser.language);
          }
          if (dbUser.timezone) setTimezone(dbUser.timezone);
          if (dbUser.shabbat_mode) setShabbatMode(dbUser.shabbat_mode);
          if (dbUser.holiday_mode) setHolidayMode(dbUser.holiday_mode);
          if (dbUser.alot_method) setAlotMethod(dbUser.alot_method as AlotMethod);
          if (dbUser.tzet_method) setTzetMethod(dbUser.tzet_method as TzetMethod);
          if (dbUser.latitude) setLocation((l) => ({ ...l, latitude: String(dbUser.latitude) }));
          if (dbUser.longitude) setLocation((l) => ({ ...l, longitude: String(dbUser.longitude) }));
          if (dbUser.elevation) setLocation((l) => ({ ...l, elevation: String(dbUser.elevation) }));
        }

        // Load call schedule
        const { data: scheduleData } = await supabase
          .from('user_settings')
          .select('setting_value')
          .eq('user_id', userId)
          .eq('setting_key', 'call_schedule')
          .maybeSingle();

        if (scheduleData?.setting_value && Array.isArray(scheduleData.setting_value)) {
          setCallSlots(scheduleData.setting_value as CallSlot[]);
        }
      } catch {
        // Silently fail — defaults are fine
      }
    }
    loadAllData();
  }, []);

  // Look up latitude/longitude (and city) from a ZIP / postal code via Zippopotam.us (free, no key).
  const lookupZip = async () => {
    const zip = location.zip.trim();
    if (!zip) return;
    setZipStatus('loading');
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${encodeURIComponent(zip)}`);
      if (!res.ok) {
        setZipStatus('error');
        return;
      }
      const data = await res.json();
      const place = data.places?.[0];
      if (!place) {
        setZipStatus('error');
        return;
      }
      setLocation((l) => ({
        ...l,
        latitude: place.latitude,
        longitude: place.longitude,
        city: place['place name'] || l.city,
        country: data.country || l.country,
      }));
      // Derive the IANA timezone from the coordinates (offline lookup).
      try {
        const tz = tzlookup(parseFloat(place.latitude), parseFloat(place.longitude));
        if (tz) setTimezone(tz);
      } catch {
        // Coordinates outside the tz map — leave timezone unchanged.
      }
      setZipStatus('idle');
    } catch {
      setZipStatus('error');
    }
  };

  const addCallSlot = () => {
    const newSlot: CallSlot = {
      id: crypto.randomUUID(),
      dayOfWeek: callSlots.length > 0 ? callSlots[callSlots.length - 1].dayOfWeek : 0,
      time: '08:00',
      enabled: true,
    };
    setCallSlots((prev) => [...prev, newSlot]);
  };

  const removeCallSlot = (id: string) => {
    setCallSlots((prev) => prev.filter((s) => s.id !== id));
  };

  const updateCallSlot = (id: string, field: keyof CallSlot, value: string | number | boolean) => {
    setCallSlots((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)),
    );
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    setSaveError('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setSaveError('Session expired. Please refresh and log in again.');
        setSaving(false);
        return;
      }
      const userId = session.user.id;

      // 1. Save profile to users table
      const { error: profileError } = await supabase
        .from('users')
        .upsert({
          id: userId,
          phone_number: profile.phone || null,
          language: language,
          timezone: timezone,
          shabbat_mode: shabbatMode,
          holiday_mode: holidayMode,
          alot_method: alotMethod,
          tzet_method: tzetMethod,
          latitude: parseFloat(location.latitude) || null,
          longitude: parseFloat(location.longitude) || null,
          elevation: parseFloat(location.elevation) || null,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'id' });

      if (profileError) {
        console.error('Profile save error:', profileError);
        setSaveError(`Failed to save profile: ${profileError.message}`);
        setSaving(false);
        return;
      }

      // 2. Save call schedule to user_settings
      const { error: scheduleError } = await supabase
        .from('user_settings')
        .upsert({
          user_id: userId,
          setting_key: 'call_schedule',
          setting_value: callSlots,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id,setting_key' });

      if (scheduleError) {
        console.error('Schedule save error:', scheduleError);
        setSaveError(`Failed to save call schedule: ${scheduleError.message}`);
        setSaving(false);
        return;
      }

      if (i18n.language !== language) i18n.changeLanguage(language);
      invalidateProfileCache();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err: any) {
      console.error('Save error:', err);
      setSaveError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const selectedLang = languages.find((l) => l.value === language);

  return (
    <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
      <header className="h-16 flex items-center justify-between px-4 md:px-6 border-b border-background-300/40 bg-cream/60 dark:bg-midnight-deep/60 backdrop-blur flex-shrink-0">
        <h2 className="font-display text-xl font-bold text-foreground-950">Settings</h2>
        <div className="flex items-center gap-1">
          <button className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-500 hover:text-gold hover:bg-gold/10 transition cursor-pointer" title="Notifications">
            <i className="ri-notification-3-line text-lg" />
          </button>
          <button
            onClick={toggleTheme}
            className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-500 hover:text-gold hover:bg-gold/10 transition cursor-pointer"
            title="Toggle dark mode"
          >
            <i className={theme === 'dark' ? 'ri-sun-line text-lg' : 'ri-moon-line text-lg'} />
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <form onSubmit={handleSave} className="max-w-2xl space-y-6">
          {/* Profile */}
          <div className="card p-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center">
                <i className="ri-user-line text-primary-500" />
              </span>
              <h3 className="text-sm font-heading font-semibold text-foreground-950">Profile</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">Full Name</label>
                <input
                  type="text"
                  value={profile.fullName}
                  onChange={(e) => setProfile((p) => ({ ...p, fullName: e.target.value }))}
                  className="w-full px-3.5 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">Email</label>
                <input
                  type="email"
                  dir="ltr"
                  value={profile.email}
                  onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
                  className="w-full px-3.5 py-2.5 text-sm rounded-md border border-background-300 bg-background-100 text-foreground-600 cursor-default"
                  readOnly
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">Phone Number</label>
                <input
                  type="tel"
                  dir="ltr"
                  value={profile.phone}
                  onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))}
                  placeholder="+1 (555) 000-0000"
                  className="w-full px-3.5 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Language */}
          <div className="card p-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-8 h-8 rounded-lg bg-accent-50 flex items-center justify-center">
                <i className="ri-global-line text-accent-500" />
              </span>
              <h3 className="text-sm font-heading font-semibold text-foreground-950">Language & Region</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">Display Language</label>
                <div className="grid grid-cols-3 gap-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.value}
                      type="button"
                      onClick={() => setLanguage(lang.value)}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-md text-sm transition cursor-pointer whitespace-nowrap border ${
                        language === lang.value
                          ? 'bg-primary-50 border-primary-300 text-primary-700'
                          : 'bg-background-50 border-background-300 text-foreground-600 hover:border-background-400'
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span className="text-xs font-medium">{lang.label}</span>
                    </button>
                  ))}
                </div>
                {selectedLang && (
                  <p className="text-[11px] text-foreground-400 mt-2">
                    Text direction: {selectedLang.dir === 'rtl' ? 'Right-to-Left' : 'Left-to-Right'}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">Timezone</label>
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent cursor-pointer"
                >
                  {timezones.map((tz) => (
                    <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Call Schedule */}
          <div className="card p-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                <i className="ri-time-line text-emerald-500" />
              </span>
              <h3 className="text-sm font-heading font-semibold text-foreground-950">Call Schedule</h3>
            </div>

            <p className="text-xs text-foreground-500 mb-4">
              Set when the system should call you each week. You can add multiple times per day.
            </p>

            {callSlots.length === 0 ? (
              <div className="text-center py-6 rounded-md border-2 border-dashed border-background-300">
                <i className="ri-phone-line text-2xl text-foreground-300 mb-2 block" />
                <p className="text-xs text-foreground-400 mb-3">No call times set yet</p>
                <button
                  type="button"
                  onClick={addCallSlot}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line" />
                  Add First Call Time
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {callSlots.map((slot) => (
                  <div
                    key={slot.id}
                    className="flex items-center gap-3 p-3 rounded-md bg-background-50 border border-background-200/70"
                  >
                    {/* Day selector */}
                    <select
                      value={slot.dayOfWeek}
                      onChange={(e) => updateCallSlot(slot.id, 'dayOfWeek', parseInt(e.target.value))}
                      className="px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent cursor-pointer min-w-0 flex-1"
                    >
                      {DAY_LABELS.map((label, idx) => (
                        <option key={idx} value={idx}>{label}</option>
                      ))}
                    </select>

                    {/* Time picker */}
                    <input
                      type="time"
                      value={slot.time}
                      onChange={(e) => updateCallSlot(slot.id, 'time', e.target.value)}
                      className="px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent w-28 flex-shrink-0"
                    />

                    {/* Enabled toggle */}
                    <button
                      type="button"
                      onClick={() => updateCallSlot(slot.id, 'enabled', !slot.enabled)}
                      className={`w-9 h-9 flex items-center justify-center rounded-md transition cursor-pointer flex-shrink-0 ${
                        slot.enabled
                          ? 'bg-emerald-100 text-emerald-600'
                          : 'bg-background-200 text-foreground-400'
                      }`}
                      title={slot.enabled ? 'Enabled' : 'Disabled'}
                    >
                      <i className={slot.enabled ? 'ri-toggle-fill' : 'ri-toggle-line'} />
                    </button>

                    {/* Delete */}
                    <button
                      type="button"
                      onClick={() => removeCallSlot(slot.id)}
                      className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition cursor-pointer flex-shrink-0"
                      title="Remove this time"
                    >
                      <i className="ri-delete-bin-line" />
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addCallSlot}
                  className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-xs font-medium rounded-md border-2 border-dashed border-background-300 text-foreground-500 hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50/50 transition cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line" />
                  Add Another Call Time
                </button>
              </div>
            )}
          </div>

          {/* Shabbat & Holidays */}
          <div className="card p-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-sm">
                🕯️
              </span>
              <h3 className="text-sm font-heading font-semibold text-foreground-950">Shabbat & Holidays</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">Shabbat Mode</label>
                <div className="space-y-2">
                  <label className="flex items-start gap-3 p-3 rounded-md border cursor-pointer transition hover:bg-background-50"
                    style={{ borderColor: shabbatMode === 'skip' ? 'oklch(var(--primary-300))' : 'oklch(var(--background-300))' }}
                  >
                    <input
                      type="radio"
                      name="shabbatMode"
                      value="skip"
                      checked={shabbatMode === 'skip'}
                      onChange={() => setShabbatMode('skip')}
                      className="mt-0.5"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground-900">Skip Calls</p>
                      <p className="text-[11px] text-foreground-500 mt-0.5">No calls on Shabbat. Day marked 🕯️ on calendar. Streak preserved.</p>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 p-3 rounded-md border cursor-pointer transition hover:bg-background-50"
                    style={{ borderColor: shabbatMode === 'post_shabbat_report' ? 'oklch(var(--primary-300))' : 'oklch(var(--background-300))' }}
                  >
                    <input
                      type="radio"
                      name="shabbatMode"
                      value="post_shabbat_report"
                      checked={shabbatMode === 'post_shabbat_report'}
                      onChange={() => setShabbatMode('post_shabbat_report')}
                      className="mt-0.5"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground-900">Post-Shabbat Report</p>
                      <p className="text-[11px] text-foreground-500 mt-0.5">No calls during Shabbat. After Havdalah, you can report your status.</p>
                    </div>
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">Holiday Mode</label>
                <div className="space-y-2">
                  <label className="flex items-start gap-3 p-3 rounded-md border cursor-pointer transition hover:bg-background-50"
                    style={{ borderColor: holidayMode === 'skip' ? 'oklch(var(--primary-300))' : 'oklch(var(--background-300))' }}
                  >
                    <input
                      type="radio"
                      name="holidayMode"
                      value="skip"
                      checked={holidayMode === 'skip'}
                      onChange={() => setHolidayMode('skip')}
                      className="mt-0.5"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground-900">Skip Calls</p>
                      <p className="text-[11px] text-foreground-500 mt-0.5">No calls on Yom Tov. Streak preserved.</p>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 p-3 rounded-md border cursor-pointer transition hover:bg-background-50"
                    style={{ borderColor: holidayMode === 'post_holiday_report' ? 'oklch(var(--primary-300))' : 'oklch(var(--background-300))' }}
                  >
                    <input
                      type="radio"
                      name="holidayMode"
                      value="post_holiday_report"
                      checked={holidayMode === 'post_holiday_report'}
                      onChange={() => setHolidayMode('post_holiday_report')}
                      className="mt-0.5"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground-900">Post-Holiday Report</p>
                      <p className="text-[11px] text-foreground-500 mt-0.5">No calls during Yom Tov. Report your status after the holiday ends.</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Zmanim Location */}
          <div className="card p-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                <i className="ri-sun-line text-amber-500" />
              </span>
              <h3 className="text-sm font-heading font-semibold text-foreground-950">Zmanim Location</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">ZIP / Postal Code</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={location.zip}
                    onChange={(e) => setLocation((l) => ({ ...l, zip: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); lookupZip(); } }}
                    placeholder="e.g. 10001"
                    className="flex-1 px-3.5 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={lookupZip}
                    disabled={zipStatus === 'loading'}
                    className="px-4 py-2.5 text-sm font-medium rounded-md bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap"
                  >
                    {zipStatus === 'loading' ? 'Looking up…' : 'Look up'}
                  </button>
                </div>
                {zipStatus === 'error' && (
                  <p className="text-[11px] text-red-500 mt-1.5">Couldn't find that ZIP code. Check it and try again (US ZIP codes supported).</p>
                )}
                {zipStatus === 'idle' && location.city && (
                  <p className="text-[11px] text-foreground-500 mt-1.5">
                    <i className="ri-map-pin-line" /> {location.city}{location.country ? `, ${location.country}` : ''}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">Latitude</label>
                <input
                  type="number"
                  step="any"
                  value={location.latitude}
                  onChange={(e) => setLocation((l) => ({ ...l, latitude: e.target.value }))}
                  className="w-full px-3.5 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">Longitude</label>
                <input
                  type="number"
                  step="any"
                  value={location.longitude}
                  onChange={(e) => setLocation((l) => ({ ...l, longitude: e.target.value }))}
                  className="w-full px-3.5 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">Elevation (meters)</label>
                <input
                  type="number"
                  step="any"
                  value={location.elevation}
                  onChange={(e) => setLocation((l) => ({ ...l, elevation: e.target.value }))}
                  className="w-full px-3.5 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">Alot HaShachar (dawn) calculation</label>
                <select
                  value={alotMethod}
                  onChange={(e) => setAlotMethod(e.target.value as AlotMethod)}
                  className="w-full px-3.5 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent cursor-pointer"
                >
                  {alotMethods.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">Tzet HaKochavim (nightfall) calculation</label>
                <select
                  value={tzetMethod}
                  onChange={(e) => setTzetMethod(e.target.value as TzetMethod)}
                  className="w-full px-3.5 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent cursor-pointer"
                >
                  {tzetMethods.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <p className="text-[11px] text-foreground-400 mt-3 flex items-center gap-1">
              <i className="ri-information-line" />
              Zmanim times are calculated based on your location using the @hebcal/zmanim library.
            </p>
          </div>

          {/* Subscription (only rendered when billing is enabled) */}
          <SubscriptionCard />

          {/* Danger Zone */}
          <div className="card p-5 border-red-200">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                <i className="ri-error-warning-line text-red-500" />
              </span>
              <h3 className="text-sm font-heading font-semibold text-foreground-950">Danger Zone</h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-md bg-red-50/50 border border-red-100">
                <div>
                  <p className="text-sm font-medium text-foreground-900">Reset All Habits</p>
                  <p className="text-[11px] text-foreground-500">Reset all habit configurations to their defaults.</p>
                </div>
                <button type="button" className="px-3 py-1.5 text-xs font-medium text-red-600 border border-red-300 rounded-md hover:bg-red-50 transition cursor-pointer whitespace-nowrap">
                  Reset
                </button>
              </div>
              <div className="flex items-center justify-between p-3 rounded-md bg-red-50/50 border border-red-100">
                <div>
                  <p className="text-sm font-medium text-foreground-900">Delete Account</p>
                  <p className="text-[11px] text-foreground-500">Permanently delete your account and all associated data.</p>
                </div>
                <button type="button" className="px-3 py-1.5 text-xs font-medium text-red-600 border border-red-300 rounded-md hover:bg-red-50 transition cursor-pointer whitespace-nowrap">
                  Delete
                </button>
              </div>
            </div>
          </div>

          {/* Save */}
          <div className="flex items-center justify-between pt-2">
            <div className="space-y-1">
              {saved && (
                <span className="text-xs text-emerald-600 flex items-center gap-1">
                  <i className="ri-check-line" /> All settings saved successfully
                </span>
              )}
              {saveError && (
                <span className="text-xs text-red-500 flex items-start gap-1">
                  <i className="ri-error-warning-line mt-px flex-shrink-0" />
                  {saveError}
                </span>
              )}
            </div>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary text-sm px-6 py-2.5 whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? (
                <span className="flex items-center gap-1.5">
                  <i className="ri-loader-4-line animate-spin" />
                  Saving...
                </span>
              ) : (
                'Save All Settings'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  return (
    <AuthGuard>
      <AppLayout>
        <SettingsContent />
      </AppLayout>
    </AuthGuard>
  );
}